import { FileText } from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import './CitationChip.css';

export interface CitationChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title'> {
  /** Source document title shown on the chip and in its accessible name. */
  documentTitle: string;
  /** Sequence number rendered as the inline marker ("[1]"). Null when the source carries no ordinal. */
  ordinal: number | null;
  /** Optional snippet surfaced via the native tooltip. */
  snippet?: string;
  /** Compact marker-only mode ("[1]") for inline use within answer prose. */
  inline?: boolean;
}

/**
 * Inline provenance marker linking a generated claim to its source. Trust is the
 * product: every answer and ata claim carries one. Use `inline` for the numbered
 * superscript inside prose; the default chip shows the document title in lists.
 */
export function CitationChip({
  documentTitle,
  ordinal,
  snippet,
  inline = false,
  className,
  type = 'button',
  ...rest
}: CitationChipProps): JSX.Element {
  const label = `Fonte ${ordinal}: ${documentTitle}`;
  return (
    <button
      type={type}
      className={cn('citation', inline && 'citation--inline', className)}
      aria-label={label}
      title={snippet ? `${documentTitle} — ${snippet}` : documentTitle}
      {...rest}
    >
      {inline ? (
        <span className="citation__ordinal">{ordinal}</span>
      ) : (
        <>
          <FileText className="citation__icon" aria-hidden="true" />
          <span className="citation__ordinal">{ordinal}</span>
          <span className="citation__title">{documentTitle}</span>
        </>
      )}
    </button>
  );
}
